/**
 * Period ("week") helpers for the weekly view.
 *
 * - Default: calendar weeks of 7 days starting on the first day of the week
 *   (unchanged behaviour).
 * - "Start from today" (wwStartFromToday = 1): rolling periods of N days
 *   (wwDaysCount: 3, 5 or 7, default 7) aligned to today. When the
 *   "date_range" events option is used, periods are aligned to the range
 *   start instead, so they line up with the range.
 *
 * Every component of the weekly view should use these helpers instead of
 * firstDayOfWeek / addWeeks / diffWeeks / isSameWeek, so arrows, dropdown,
 * "This week" button and day columns always agree on what a period is.
 */

import { useContext, useMemo } from "react";
import WidgetSettingsContext from "../contexts/WidgetSettingsContext";
import { firstDayOfWeek, isSameDay, startOfDay } from "../helpers/date";
import { getDateRange } from "../helpers/calendar";

const ALLOWED_DAYS = [3, 4, 5, 6, 7];
const DAY_MS = 24 * 60 * 60 * 1000;

const useWeekPeriod = () => {
  const widgetSettings = useContext(WidgetSettingsContext);

  const isRolling = Number.parseInt(widgetSettings["wwStartFromToday"]) === 1;
  const daysSetting = Number.parseInt(widgetSettings["wwDaysCount"]);
  const days =
    isRolling && ALLOWED_DAYS.includes(daysSetting) ? daysSetting : 7;

  const eventsNumberOption = widgetSettings["eventsNumberOption"];
  const eventsNumberCount = widgetSettings["eventsNumber"];

  // Computed as a timestamp so the memo below stays stable during the day.
  let anchorTime = null;
  if (isRolling) {
    let anchorDate = new Date();

    if (eventsNumberOption === "date_range") {
      const [rangeStart] = getDateRange(eventsNumberOption, eventsNumberCount);
      if (rangeStart) {
        anchorDate = rangeStart;
      }
    }

    anchorTime = startOfDay(anchorDate).getTime();
  }

  return useMemo(() => {
    const anchor = anchorTime === null ? null : new Date(anchorTime);

    /**
     * First day of the period containing `date`. Always returns a new Date.
     * @param {Date} date
     * @returns {Date}
     */
    const periodStart = (date) => {
      if (!anchor) {
        // Calendar mode: unchanged behaviour.
        return new Date(firstDayOfWeek(new Date(date)));
      }

      // Rolling mode: N-day buckets aligned to the anchor.
      // Math.round absorbs DST shifts; Math.floor handles dates before the anchor.
      const diffDays = Math.round((startOfDay(date) - anchor) / DAY_MS);
      const start = new Date(anchor);
      start.setDate(anchor.getDate() + Math.floor(diffDays / days) * days);
      return start;
    };

    /**
     * Last day of the period containing `date`.
     * @param {Date} date
     * @returns {Date}
     */
    const periodEnd = (date) => {
      const end = periodStart(date);
      end.setDate(end.getDate() + days - 1);
      return end;
    };

    /**
     * Start of the period `n` periods away from the one containing `date`.
     * @param {Date} date
     * @param {number} n
     * @returns {Date}
     */
    const addPeriods = (date, n) => {
      const start = periodStart(date);
      start.setDate(start.getDate() + n * days);
      return start;
    };

    /**
     * @param {Date} a
     * @param {Date} b
     * @returns {boolean}
     */
    const isSamePeriod = (a, b) => isSameDay(periodStart(a), periodStart(b));

    /**
     * Number of periods from b to a (positive when a is later).
     * Math.round absorbs DST shifts.
     * @param {Date} a
     * @param {Date} b
     * @returns {number}
     */
    const diffPeriods = (a, b) =>
      Math.round((periodStart(a) - periodStart(b)) / (days * DAY_MS));

    return {
      isRolling,
      days,
      anchor,
      periodStart,
      periodEnd,
      addPeriods,
      isSamePeriod,
      diffPeriods,
      periodMs: days * DAY_MS,
    };
  }, [anchorTime, days, isRolling]);
};

export default useWeekPeriod;
